import { redirect, type RouteObject } from 'react-router';
import { checkAuthAndRedirect } from '@/helper/auth.helper.ts';
import { AUTH_ROUTE_BASE, BASE_PATHS, ROUTES } from '@/constants/route.constant.ts';
import Login from '@/modules/auth/login';


/**
 * Defines the route hierarchy and behaviors for authentication-related routes.
 *
 * - The root loader `redirectToLogin` ensures that access to the authenticated route tree
 *   is secured and redirects unauthorized users to the login page.
 *
 * - Nested children routes:
 *   - `AUTH_BASE_ROUTE`: Acts as the base path for all authentication-related routes.
 *     - `AUTH_ROUTES.LOGIN`: Points to the login page, rendering the `LoginPage` component.
 *     - Default route (with `index: true`): Redirects the root of `AUTH_BASE_ROUTE`
 *       directly to the login page.
 *     - Catch-all route (`path: '*'`): Redirects all unmatched routes under the
 *       authentication path to the login page.
 *
 * This structure enforces consistent redirection behavior for unauthenticated users and ensures
 * all invalid paths under the authentication base route are rerouted to the login page.
 *
 * @type {RouteObject}
 */
const authRoutes: RouteObject = {
  loader: () => checkAuthAndRedirect('unauthenticated'),
  children: [
    {
      path: BASE_PATHS.AUTH,
      children: [
        {
          path: AUTH_ROUTE_BASE.LOGIN,
          Component: Login,
        },
        {
          path: AUTH_ROUTE_BASE.CALLBACK,
          Component: Login,
        },
        {
          index: true, // Redirect the root of AUTH_BASE_ROUTE to log in
          loader: () => redirect(ROUTES.AUTH.LOGIN),
        },
        {
          path: '*',
          loader: () => redirect(ROUTES.AUTH.LOGIN),
        },
      ],
    },
  ],
};

export default authRoutes;
